/**
 * Auth state. Adapted from `bimgo-ai-web/client/src/contexts/AuthProvider.tsx`.
 *
 * It owns the one decision every screen downstream depends on — signed in or
 * not — and exposes the two actions that change it, `signIn` and `signOut`, so a
 * page never reaches for the Supabase client itself.
 *
 * Kept from the port: `recovering` (a password-recovery link sets it, item J
 * routes on it) and the `ready` flag, which is distinct from "signed out" —
 * rendering the login form before the first `getSession()` resolves flashes it
 * at an already-signed-in user on every launch.
 */
import { createContext, useContext, useEffect, useState } from "react";
import type { Session } from "@supabase/supabase-js";
import { isConfigured } from "../lib/config";
import { supabase } from "../lib/supabaseClient";
import { useSessionGrace, useSessionGuard } from "../hooks/useSessionGuard";
import { clearGrace } from "../lib/authSession";
import { SignOutWarningBanner } from "../components/SignOutWarningBanner";
import { retainSession, setCurrentAccessToken } from "../lib/accessToken";

interface AuthValue {
  session: Session | null;
  user: Session["user"] | null;
  /** True when the user arrived via a password-recovery link. Item J routes on it. */
  recovering: boolean;
  /** Null until the first getSession() resolves — distinct from "signed out". */
  ready: boolean;
  /** Sign in. Returns an error message, or null on success. */
  signIn: (email: string, password: string) => Promise<string | null>;
  /** Sign out and return to the login page. */
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthValue>({
  session: null,
  user: null,
  recovering: false,
  ready: false,
  signIn: async () => "auth provider not mounted",
  signOut: async () => undefined,
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [recovering, setRecovering] = useState(false);
  const [ready, setReady] = useState(false);

  // The single-session guard (mirrored from bimgo-ai-web): sign this device out
  // a grace period after the account is used elsewhere (#75), instead of at
  // once — or, before the guard existed, whenever the next API call noticed.
  useSessionGuard(session);
  const grace = useSessionGrace();

  useEffect(() => {
    // An unconfigured build cannot construct a client. Guard here as well as in
    // App, because this provider runs first and a throw would beat the friendly
    // message to the screen (measured — Corrections in the desktop notes).
    if (!isConfigured()) {
      setReady(true);
      return;
    }

    const client = supabase();

    void client.auth.getSession().then(({ data }) => {
      setCurrentAccessToken(data.session?.access_token ?? null);
      setSession(data.session);
      setReady(true);
    });

    // Every event updates the token store; only a *different* sign-in replaces
    // the session object. Supabase re-emits SIGNED_IN / TOKEN_REFRESHED on
    // window focus (and the session guard's probe rotates the token on focus),
    // and a new object each time made every token-keyed hook refetch — the app
    // "refreshed" whenever it was focused. See `lib/accessToken.ts`.
    // Tracked here, not read from `session`: this effect runs once, so the
    // state it closed over is the initial null and would call every event a
    // "different user" — including the hourly TOKEN_REFRESHED, which would end
    // the grace on every rotation.
    let previousUserId: string | null = null;
    const { data } = client.auth.onAuthStateChange((event, next) => {
      setCurrentAccessToken(next?.access_token ?? null);
      // However the session ended — the grace deadline firing, a manual "Sign
      // out", an expired token — the countdown is over. Without this a user who
      // signed out by hand mid-grace would carry the warning onto the login page.
      // A different user signing in ends it for the same reason.
      const nextUserId = next?.user?.id ?? null;
      const userChanged = nextUserId !== null && previousUserId !== null && nextUserId !== previousUserId;
      if (event === "SIGNED_OUT" || userChanged) clearGrace();
      previousUserId = nextUserId;
      setSession((prev) => retainSession(prev, next));
      setReady(true);
      const isRecovery =
        window.location.hash.includes("type=recovery") || event === "PASSWORD_RECOVERY";
      if (isRecovery) setRecovering(true);
    });

    return () => data.subscription.unsubscribe();
  }, []);

  async function signIn(email: string, password: string): Promise<string | null> {
    try {
      const { error } = await supabase().auth.signInWithPassword({ email, password });
      return error ? error.message : null;
    } catch (e) {
      // Unconfigured build, or a client that failed to construct.
      return e instanceof Error ? e.message : String(e);
    }
  }

  async function signOut(): Promise<void> {
    try {
      await supabase().auth.signOut();
    } catch {
      // Even if the network sign-out fails, drop the local session so the user
      // is not stuck signed in.
      setSession(null);
    }
  }

  return (
    <AuthContext.Provider
      value={{ session, user: session?.user ?? null, recovering, ready, signIn, signOut }}
    >
      {/* Only while signed in: a deadline that outlives the session (a network
          failure at confirmation, say) must not decorate the login page. */}
      {session && grace ? <SignOutWarningBanner /> : null}
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthValue {
  return useContext(AuthContext);
}
